import { AxiosError } from 'axios';
import { Dispatch, SetStateAction } from 'react';
import { ApiErrorCode } from './ApiError';

const FORBIDDEN_FALLBACK =
  "You don't have permission to do that. If you think this is wrong, contact a Kit Keeper admin.";
const UNAUTHORIZED_FALLBACK =
  'Your session has expired. Please sign in again to continue.';
const GENERIC_FALLBACK = 'An unexpected error occurred. Please try again.';

const IS_DEBUG =
  process.env.NODE_ENV === 'development' ||
  (typeof window !== 'undefined' &&
    window.location?.hostname === 'test.kitkeeper.co.uk');

function readStoredFailedRequestId(): string | undefined {
  try {
    const value = localStorage.getItem('last_failed_request_id');
    return value ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Ktor's own reason phrases. `HTTPException` defaults its message to
 * `statusCode.description`, so a bare `throw BadRequestException()` puts the literal
 * string "Bad Request" in the envelope's `error` field. That is not a message, and it must
 * not reach a customer now that the backend's sentence is preferred over ours.
 */
const HTTP_REASON_PHRASES = new Set([
  'accepted',
  'not modified',
  'bad request',
  'unauthorized',
  'payment required',
  'forbidden',
  'not found',
  'conflict',
  'gone',
  'payload too large',
  'unprocessable entity',
  'too many requests',
  'internal server error',
  'bad gateway',
  'service unavailable',
]);

function usableBackendMessage(message: unknown): string | undefined {
  if (typeof message !== 'string') return undefined;
  const trimmed = message.trim();
  if (!trimmed) return undefined;
  if (HTTP_REASON_PHRASES.has(trimmed.toLowerCase())) return undefined;
  return trimmed;
}

/**
 * Wording that is better than anything the backend could send, so it wins outright.
 *
 * Only three kinds of thing qualify. Infrastructure failures, where the backend's sentence
 * describes our problem rather than the customer's and we would rather give them a
 * reference they can quote; and an expired session, where "your session has expired" is
 * what the person needs to read whatever the server called it.
 *
 * **Everything else defers to the backend**, and that is the whole point of this function
 * being small. Until 2026-09-30 it covered every code, so a canned sentence replaced the
 * specific one on the two codes that carry almost all of the traffic:
 *
 *  - `CONFLICT` became "This item was updated by someone else. Please refresh and try
 *    again." — which describes an optimistic-concurrency clash, and **not one throw site in
 *    storage-api is one**. Signing up with an address that already has an account produced
 *    exactly that sentence on the main `/register` page, so a returning customer was told
 *    to refresh instead of to log in. `OxfordPackages` handled the same 409 properly,
 *    which is how the difference showed.
 *  - `VALIDATION_FAILED` became "Please check the details you've entered and try again."
 *    The backend had already said "This password has been seen in a known data breach.
 *    Please choose a different one.", or "Cannot change order collection details within 3
 *    working days of collection" — the actual reason, and the only version a customer can
 *    act on.
 */
function overridingMessage(
  code: ApiErrorCode,
  requestId?: string,
): string | undefined {
  switch (code) {
    case 'UNAUTHENTICATED':
      return UNAUTHORIZED_FALLBACK;
    case 'INTERNAL_ERROR':
      return serverFallback(requestId);
    case 'EXTERNAL_FAILURE':
      return "We couldn't reach an external service right now. Please try again in a moment.";
    case 'MAINTENANCE':
      return 'Kit Keeper is currently undergoing maintenance. Please try again shortly.';
    default:
      return undefined;
  }
}

/**
 * What to say when the backend sent no usable sentence of its own.
 *
 * `default` covers a code this build has never heard of. `ApiErrorCode` is a hand-kept copy
 * of storage-api's `ErrorCode` and the two have drifted — `ACCOUNT_DEACTIVATED` shipped on
 * the backend in September and was still missing here in the same month. The type says this
 * switch is exhaustive; the value came off the wire, where a type is a wish. Without the
 * default an unknown code fell off the end, returned `undefined`, and the customer was
 * shown no message at all.
 */
function fallbackForCode(code: ApiErrorCode): string {
  switch (code) {
    case 'FORBIDDEN':
      return FORBIDDEN_FALLBACK;
    case 'NOT_FOUND':
      return 'The item you were looking for could not be found.';
    case 'VALIDATION_FAILED':
      return "Please check the details you've entered and try again.";
    case 'CONFLICT':
      return 'That could not be completed because something has changed. Please refresh and try again.';
    case 'RATE_LIMITED':
      return 'Too many requests. Please wait a moment and try again.';
    case 'ACCOUNT_DEACTIVATED':
      return 'This account has been deactivated. Please contact Kit Keeper operations.';
    case 'PAYMENT_METHOD_REQUIRED':
      return "There's no payment method on your account. Please add a card and try again.";
    default:
      return GENERIC_FALLBACK;
  }
}

function serverFallback(requestId?: string): string {
  return requestId
    ? `Something went wrong on the server. Please try again. Reference: ${requestId}.`
    : 'Something went wrong on the server. Please try again.';
}

export default function errorMessage(
  setNetworkError: Dispatch<SetStateAction<string | undefined>>,
) {
  return (error: string | Error | AxiosError) => {
    // This is the last thing standing between a failure and what the customer reads, so
    // it must not be able to fail itself. It could: `JSON.stringify` throws on a circular
    // structure, and `null.hasOwnProperty` throws outright — and both arrive here, because
    // the caller is a `catch` block and a `catch` binding is whatever was thrown.
    let jsonError: Record<string, unknown> = {};
    try {
      jsonError = JSON.parse(JSON.stringify(error ?? {})) ?? {};
    } catch {
      jsonError = {};
    }
    if (typeof jsonError !== 'object' || jsonError === null) jsonError = {};
    let userMessage: string;
    let technicalDetail: string | undefined;

    if (jsonError.hasOwnProperty('config')) {
      const axiosError = error as AxiosError;
      const status = axiosError.response?.status;
      const data = axiosError.response?.data;
      const code: ApiErrorCode | undefined = data?.code;
      const envelopeRequestId: string | undefined = data?.requestId;
      // `error`, and only `error`. The envelope has no `message` field — see
      // `.claude/context/api-integration.md`. Reading one was a live defect on the
      // return-timeslot path until 2026-09-28.
      const backendMessage: string | undefined = data?.error;

      technicalDetail = backendMessage;

      if (code) {
        // Order matters, and it used to be the other way round. Our wording wins only
        // where it is genuinely better (see `overridingMessage`); otherwise the backend's
        // own sentence is the specific one and the customer needs it. A canned sentence
        // is the last resort, not the first answer — and something must always be said,
        // because showing nothing is what the old exhaustive switch did.
        userMessage =
          overridingMessage(code, envelopeRequestId) ||
          usableBackendMessage(backendMessage) ||
          fallbackForCode(code);
      } else if (status && status >= 500) {
        const fallbackRequestId =
          envelopeRequestId || readStoredFailedRequestId();
        userMessage = serverFallback(fallbackRequestId);
      } else if (status === 403) {
        userMessage = usableBackendMessage(backendMessage) || FORBIDDEN_FALLBACK;
      } else if (status === 401) {
        userMessage = usableBackendMessage(backendMessage) || UNAUTHORIZED_FALLBACK;
      } else {
        userMessage = usableBackendMessage(backendMessage) || GENERIC_FALLBACK;
      }
    } else if (error instanceof Error) {
      // `Error.message` is not enumerable, so the JSON round-trip above turns a plain
      // `new Error('Offline')` into `{}` — which meant the `hasOwnProperty('message')`
      // branch below could never match one, and the raw Error object was handed to a
      // setter typed `string | undefined` and then rendered. React throws on an object
      // child, so the customer got a blank screen rather than a message.
      userMessage = error.message;
      technicalDetail = userMessage;
    } else if (jsonError && typeof jsonError === 'object' && 'message' in jsonError) {
      userMessage = String((jsonError as { message: unknown }).message);
      technicalDetail = userMessage;
    } else if (typeof error === 'string') {
      userMessage = error;
      technicalDetail = userMessage;
    } else {
      // Something that is neither an axios error, an Error, nor a string. There is
      // nothing to read, and anything but a string here ends up rendered.
      userMessage = GENERIC_FALLBACK;
    }

    if (IS_DEBUG && technicalDetail && technicalDetail !== userMessage) {
      console.error('[errorMessage] technical detail:', technicalDetail);
    } else if (IS_DEBUG) {
      console.error(userMessage);
    }

    setNetworkError(userMessage);
  };
}
